import {createContext, PropsWithChildren} from 'react';
import {ReadonlyURLSearchParams, useRouter} from 'next/navigation';
import {AppRouterInstance} from 'next/dist/shared/lib/app-router-context.shared-runtime';
import {useTranslations} from 'next-intl';

export interface AppRouterRef {
  baseUrl?: string;
  rerender: () => void;
  pathname: string;
  searchParams: ReadonlyURLSearchParams;
  router: AppRouterInstance;
}

export const useAppRouter = () => {
  // appRouter.watch();
  //
  return appRouter;
};

const appRouterRef: AppRouterRef = {} as any;

export interface AppRouterProps extends PropsWithChildren {
  baseUrl?: string;
}

const filterOutBaseUrl = (path: string, baseUrl = '') => {
  if (baseUrl && path.startsWith(baseUrl)) {
    path = path.replace(baseUrl, '');
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
  }
  return path;
};

export const AppRouterProvider = ({children, baseUrl}: AppRouterProps) => {
  appRouterRef.router = useRouter();
  appRouterRef.baseUrl = baseUrl;

  return children;
};

export class AppRouter {
  readonly #ref: AppRouterRef;

  // readonly #lastCheck: Record<string, unknown> = {};

  // readonly watch: () => void;

  // #rerender!: () => void;

  constructor(ref: AppRouterRef) {
    this.#ref = ref;

    // const store = create<{ ref: {} }>((set) => {
    //   this.#rerender = debounce(() => set({ ref: {} }));
    //   return {
    //     ref: {},
    //   };
    // });
    //
    // this.watch = () => {
    //   store((s) => s.ref);
    // };
  }

  get pathname() {
    if (this.#ref.pathname) {
      return this.#ref.pathname;
    }

    if (typeof window === 'undefined') {
      throw new Error('missing window');
    }

    return filterOutBaseUrl(window.location.pathname, this.#ref.baseUrl);
  }

  set pathname(pathname: string) {
    console.debug('[SET PATHNAME] ' + pathname);
    const url = this.createUrl(pathname);
    this.#ref.pathname = url.split('?')[0];
  }

  get baseUrl() {
    return this.#ref.baseUrl;
  }

  get searchParams() {
    return new URLSearchParams(window.location.search);
  }

  get isNavigating() {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      filterOutBaseUrl(window.location.pathname, this.#ref.baseUrl) !==
      this.#ref.pathname
    );
  }

  readonly isNavigatingTo = (path: string) => {
    if (typeof window === 'undefined') {
      return false;
    }

    return this.#ref.pathname === new URL(path, window.location.href).pathname;
  };

  readonly prefetch = (...args: Parameters<AppRouterInstance['prefetch']>) => {
    return this.#ref.router.prefetch(...args);
  };

  readonly back = () => {
    return this.#ref.router.back();
  };

  readonly refresh = () => {
    return this.#ref.router.refresh();
  };

  readonly forward = () => {
    return this.#ref.router.forward();
  };

  readonly redirectUrl = (path: string) => {
    const search = this.searchParams;
    return this.createUrl(path, {
      redirect: encodeURIComponent(this.pathname + search.toString()),
    });
  };

  readonly redirectBackUrl = (defaultPath?: string) => {
    let path: string | undefined | null = this.searchParams.get('redirect');
    console.debug(
      'get redirect back search',
      window.location.href,
      this.searchParams.toString(),
      path
    );

    if (path) {
      path = decodeURIComponent(path);
    } else {
      path = defaultPath ? this.createUrl(defaultPath) : undefined;
    }

    if (!path) {
      throw new Error('missing redirect back path');
    }

    return filterOutBaseUrl(decodeURIComponent(path), this.#ref.baseUrl);
  };

  readonly createUrl = (
    path: string,
    updateSearch: Record<string, string> = {}
  ) => {
    const url = new URL(path, window.location.href);
    const search = this.searchParams;

    console.debug(
      'creating url: ',
      path,
      window.location.href,
      search.toString()
    );

    Object.entries(updateSearch).forEach(([key, value]) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    });

    // forward the redirect from the previous URL
    if (search.has('redirect')) {
      url.searchParams.set('redirect', search.get('redirect')!);
    }

    console.debug(
      'new url: ',
      url.href,
      url.searchParams.size,
      url.search,
      filterOutBaseUrl(url.pathname, this.#ref.baseUrl) + url.search
    );

    return filterOutBaseUrl(url.pathname, this.#ref.baseUrl) + url.search;
  };

  readonly redirectBack = (path: string) => {
    try {
      const url = this.redirectBackUrl(path);
      console.debug('[REDIRECTING BACK] ' + url);
      this.#ref.router.replace(url);
      this.#ref.pathname = url.split('?')[0];
    } catch (e) {
      console.error(e);
      this.back();
    }
  };

  readonly redirect = (path: string) => {
    console.debug('[REDIRECTING] ' + path);
    const url = this.redirectUrl(path);
    this.#ref.router.push(url);
    this.#ref.pathname = url.split('?')[0];
  };

  readonly redirectReplace = (path: string) => {
    console.debug('[REDIRECTING REPLACE] ' + path);
    const url = this.redirectUrl(path);
    this.#ref.router.replace(url);
    this.#ref.pathname = url.split('?')[0];
  };

  readonly push = (path: string) => {
    console.debug('[PUSH] ' + path);
    const urlString = this.createUrl(path);
    this.#ref.router.push(urlString);
    this.#ref.pathname = urlString.split('?')[0];
  };

  readonly replace = (path: string) => {
    console.debug('[REPLACE] ' + path);
    const urlString = this.createUrl(path);
    this.#ref.router.replace(urlString);
    this.#ref.pathname = urlString.split('?')[0];
  };
}

export const appRouter = new AppRouter(appRouterRef);
export const AppRouterContext = createContext(null);
